import React, { useState } from 'react'
import { Card, Flex, Text } from '@tremor/react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@tremor/react'
import { BarChart3, Star, Zap, TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from '@/components/icons'
import { cn } from '@/lib/utils'
import type { OperatorPerformance } from '../types'
import { CONVERSION_THRESHOLDS } from '../types'

interface PerformanceTableProps {
  operators: OperatorPerformance[]
  itemsPerPage?: number
}

// Get progress bar color based on conversion rate
function getConversionColor(rate: number): string {
  if (rate >= CONVERSION_THRESHOLDS.ELITE) return 'bg-violet-500'
  if (rate >= CONVERSION_THRESHOLDS.STRONG) return 'bg-emerald-500'
  if (rate >= CONVERSION_THRESHOLDS.APPROACHING) return 'bg-amber-500'
  return 'bg-gray-400'
}

// Get quality score segment colors (4 segments)
function getQualitySegments(score: number): string[] {
  const segments: string[] = []

  // Segment 1: 0-25 (gray when filled)
  segments.push(score >= 25 ? 'bg-gray-400' : 'bg-gray-200')
  // Segment 2: 25-50 (yellow when filled)
  segments.push(score >= 50 ? 'bg-yellow-400' : 'bg-gray-200')
  // Segment 3: 50-75 (orange when filled)
  segments.push(score >= 75 ? 'bg-orange-400' : 'bg-gray-200')
  // Segment 4: 75-100 (green when filled)
  segments.push(score >= 85 ? 'bg-emerald-500' : 'bg-gray-200')

  return segments
}

// Get multiplier badge style
function getMultiplierStyle(multiplier: number): { bg: string; text: string } {
  if (multiplier >= 125) return { bg: 'bg-violet-100', text: 'text-violet-700' }
  if (multiplier >= 112) return { bg: 'bg-emerald-100', text: 'text-emerald-700' }
  if (multiplier >= 105) return { bg: 'bg-amber-100', text: 'text-amber-700' }
  return { bg: 'bg-gray-100', text: 'text-gray-600' }
}

export function PerformanceTable({ operators, itemsPerPage = 6 }: PerformanceTableProps) {
  const [currentPage, setCurrentPage] = useState(1)

  const totalPages = Math.ceil(operators.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentOperators = operators.slice(startIndex, endIndex)

  const goToPrevPage = () => setCurrentPage(prev => Math.max(1, prev - 1))
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1))

  return (
    <Card className="border-l-4 border-l-slate-500 shadow-md hover:shadow-lg p-0 overflow-hidden animate-fade-in transition-shadow duration-200 bg-gradient-to-br from-slate-50/30 via-white to-white border-t border-r border-b border-slate-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-100 bg-gradient-to-r from-slate-50 to-transparent">
        <Flex alignItems="center" className="gap-3">
          <div className="p-2 rounded-lg bg-slate-100 text-slate-600">
            <BarChart3 className="w-5 h-5" />
          </div>
          <Text className="text-zinc-950 font-semibold text-lg">
            Team Performance Overview
          </Text>
        </Flex>
      </div>

      <Table>
        <TableHead>
          <TableRow className="bg-zinc-50/50">
            <TableHeaderCell className="pl-6 font-semibold text-zinc-700">
              Operator
            </TableHeaderCell>
            <TableHeaderCell className="text-center font-semibold text-zinc-700">
              Calls
            </TableHeaderCell>
            <TableHeaderCell className="text-center font-semibold text-zinc-700">
              Opps
            </TableHeaderCell>
            <TableHeaderCell className="text-center font-semibold text-zinc-700">
              Booked
            </TableHeaderCell>
            <TableHeaderCell className="font-semibold text-zinc-700 min-w-[180px]">
              Conversion
            </TableHeaderCell>
            <TableHeaderCell className="font-semibold text-zinc-700 min-w-[160px]">
              Quality Score
            </TableHeaderCell>
            <TableHeaderCell className="text-center pr-6 font-semibold text-zinc-700">
              Multiplier
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentOperators.map((operator) => {
            const isTopPerformer = operator.rank === 1
            const conversionColor = getConversionColor(operator.conversionRate)
            const qualitySegments = getQualitySegments(operator.qualityScore)
            const multiplierStyle = getMultiplierStyle(operator.bonusMultiplier)

            return (
              <TableRow
                key={operator.id}
                className="hover:bg-zinc-50/80 transition-colors border-b border-zinc-100"
              >
                {/* Operator */}
                <TableCell className="pl-6 py-4">
                  <Flex alignItems="center" className="gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-sm">
                        <span className="text-white text-sm font-bold">
                          {operator.initials}
                        </span>
                      </div>
                      {isTopPerformer && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white">
                          <Star className="w-3 h-3 text-white fill-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-zinc-900">
                        {operator.name}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {operator.role || 'Agent'}
                      </span>
                    </div>
                  </Flex>
                </TableCell>

                {/* Calls */}
                <TableCell className="text-center py-4">
                  <span className="font-mono font-bold text-zinc-800 text-lg">
                    {operator.totalCalls}
                  </span>
                </TableCell>

                {/* Opportunities */}
                <TableCell className="text-center py-4">
                  <span className="font-mono font-medium text-zinc-600 text-lg">
                    {operator.opportunities}
                  </span>
                </TableCell>

                {/* Bookings */}
                <TableCell className="text-center py-4">
                  <span className="font-mono font-medium text-zinc-600 text-lg">
                    {operator.bookings}
                  </span>
                </TableCell>

                {/* Conversion with Progress Bar and Trend */}
                <TableCell className="py-4">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-zinc-800">
                        {operator.conversionRate.toFixed(1)}%
                      </span>
                      {operator.conversionTrend !== undefined && operator.conversionTrend !== null && (
                        <span className={cn(
                          "flex items-center gap-0.5 text-xs font-medium",
                          operator.conversionTrend >= 0 ? "text-emerald-600" : "text-red-500"
                        )}>
                          {operator.conversionTrend >= 0 ? (
                            <TrendingUp className="w-3 h-3" />
                          ) : (
                            <TrendingDown className="w-3 h-3" />
                          )}
                          {operator.conversionTrend >= 0 ? '+' : ''}{operator.conversionTrend}%
                        </span>
                      )}
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={cn("h-full rounded-full transition-all", conversionColor)}
                        style={{ width: `${Math.min(operator.conversionRate, 100)}%` }}
                      />
                    </div>
                  </div>
                </TableCell>

                {/* Quality Score with Segmented Bar */}
                <TableCell className="py-4">
                  <div className="flex flex-col gap-1.5">
                    <span className="font-mono font-bold text-zinc-800">
                      {operator.qualityScore}<span className="text-zinc-400 font-normal">/100</span>
                    </span>
                    {/* Segmented Bar */}
                    <div className="flex gap-1">
                      {qualitySegments.map((color, i) => (
                        <div
                          key={i}
                          className={cn("h-2 flex-1 rounded-sm transition-all", color)}
                        />
                      ))}
                    </div>
                  </div>
                </TableCell>

                {/* Multiplier Badge */}
                <TableCell className="text-center pr-6 py-4">
                  <span className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono font-bold text-sm",
                    multiplierStyle.bg,
                    multiplierStyle.text
                  )}>
                    {operator.bonusMultiplier >= 112 && (
                      <Zap className="w-4 h-4" />
                    )}
                    {operator.bonusMultiplier}%
                  </span>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {operators.length === 0 && (
        <div className="py-12 text-center">
          <Text className="text-zinc-400">No operators found</Text>
        </div>
      )}

      {/* Pagination */}
      {operators.length > itemsPerPage && (
        <div className="px-6 py-4 border-t border-zinc-100 bg-zinc-50/50">
          <Flex justifyContent="between" alignItems="center">
            <Text className="text-sm text-zinc-600">
              Showing {startIndex + 1}-{Math.min(endIndex, operators.length)} of {operators.length} operators
            </Text>
            <Flex className="gap-2">
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className={cn(
                  "flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                  currentPage === 1
                    ? "text-zinc-400 bg-zinc-100 cursor-not-allowed"
                    : "text-zinc-700 bg-white border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300"
                )}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={cn(
                  "flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors",
                  currentPage === totalPages
                    ? "text-zinc-400 bg-zinc-100 cursor-not-allowed"
                    : "text-zinc-700 bg-white border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300"
                )}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </Flex>
          </Flex>
        </div>
      )}
    </Card>
  )
}
